import { cellAtWorld, heroWalkable } from '../grid.ts';
import { idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Hero } from '../types.ts';
import type { CairnRules, CairnState } from '../aster-types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import type { NightmareType } from '@somnhold/content';

/**
 * The knots, small (docs/sendas/explorador.md, chapter 1, act 1, «El canto de los Surcos»; §4, «Los nudos»; the ground
 * in battle/aster-layout.ts). Cairns stand all over the rim of the Furrows; three are Aster's. The hero is sent to a
 * cairn (the `mark` command, its index) and reads it standing `read` by it: Aster's first shows its rings on the ground
 * (a hex ring a hundred paces apart) and its cord in the plate; the one its knot names is Aster's too, and its cord
 * names the last. A cairn that isn't comes apart with a noise and wakes a band (slingers off the rim, crows off the
 * sea), bigger with each. Won with the last found. Slingers and crows come the while: the rule is the knot, the
 * pressure is the band.
 *
 * Cost: a handful of cairns, looked at once a tick; what comes hunts by the common rule.
 */

const fresh = (r: CairnRules): CairnState => ({ step: 0, reading: -1, st: r.cairns.map(() => 0), job: -1, held: 0, hp: 0, at: -1, wrongs: 0, next: { sling: r.sling.first, crow: r.crows.first }, done: -1 });

/** Turns of a sixth (cos, sin in thousandths): straight on, then either way round. */
const SIXTHS: [number, number][] = [
  [1000, 0],
  [500, 866],
  [500, -866],
  [-500, 866],
  [-500, -866],
  [-1000, 0],
];

/** A walkable point by a cairn, on the hero's side of it (the cairn's own cell is stone), else round it. */
export const besideCairn = (s: BattleState, cx: number, cy: number, hx: number, hy: number, d = 850): { x: number; y: number } => {
  const dx = hx - cx;
  const dy = hy - cy;
  const n = imax(1, isqrt(dx * dx + dy * dy));
  const ux = idiv(dx * 1000, n);
  const uy = idiv(dy * 1000, n);
  for (const [c, sn] of SIXTHS) {
    const x = cx + idiv(idiv(ux * c - uy * sn, 1000) * d, 1000);
    const y = cy + idiv(idiv(ux * sn + uy * c, 1000) * d, 1000);
    if (heroWalkable(s, cellAtWorld(s, x, y))) return { x, y };
  }
  return { x: cx + idiv(dx * d, n), y: cy + idiv(dy * d, n) };
};

/** The hero sent to a cairn (its index): beside it, to read it. */
export const cairnOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.cairns;
  const w = s.senda?.cairns;
  if (!r || !w || at < 0 || at >= r.cairns.length || w.done >= 0) return;
  const c = r.cairns[at] as CairnRules['cairns'][number];
  w.job = at;
  w.held = 0;
  const p = besideCairn(s, c.x, c.y, h.x, h.y);
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** A band in: `n` of a kind at the rim's cells (from the seed's draw), their life over the act's. */
const band = (s: BattleState, r: CairnRules, kind: NightmareType, n: number, near?: { x: number; y: number }): void => {
  for (let k = 0; k < n; k++) {
    let p = r.rim[rngInt(s.rng, r.rim.length)] as { x: number; y: number };
    if (near && kind === 'crow') p = { x: near.x + 6000 + k * 500, y: near.y - 2000 + k * 700 };
    const e = spawnEnemy(s, kind, 1, false, 0, p.x, p.y);
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
    e.anim = 'walk';
  }
};

/** A cairn read: Aster's next (the way on), or a false one come down. */
const touch = (s: BattleState, r: CairnRules, w: CairnState, i: number, cue: (c: string) => void): void => {
  if (w.st[i] !== 0) return;
  const c = r.cairns[i] as CairnRules['cairns'][number];
  if (i === r.way[w.step]) {
    w.st[i] = 1;
    w.reading = i;
    w.at = s.tick;
    w.step++;
    ev(s, { t: 'senda', kind: w.step === 1 ? 'cairnRead' : 'cairnRight', npc: '', x: c.x, y: c.y, n: i });
    if (w.step > 1) cue(`right${w.step - 1}`);
    if (w.step >= r.way.length) w.done = s.tick;
    return;
  }
  // A false one: it comes apart with a noise, and what heard it comes.
  w.st[i] = 2;
  w.wrongs++;
  ev(s, { t: 'senda', kind: 'cairnFalse', npc: '', x: c.x, y: c.y, n: i });
  cue(`wrong${w.wrongs}`);
  const more = (w.wrongs - 1) * r.wrong.more;
  band(s, r, 'slinger', r.wrong.sling + more);
  band(s, r, 'crow', r.wrong.crows + more, c);
};

export const cairnsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.cairns;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.cairns ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The rim's slingers and the sea's crows, the while.
  if (s.waveT >= w.next.sling) {
    w.next.sling = s.waveT + r.sling.every;
    band(s, r, 'slinger', r.sling.n);
    cue('sling');
  }
  if (s.waveT >= w.next.crow && h) {
    w.next.crow = s.waveT + r.crows.every;
    band(s, r, 'crow', r.crows.n, h);
  }
  if (!h || w.job < 0) return;
  const c = r.cairns[w.job] as CairnRules['cairns'][number];
  // A stone on the hero while it unties the cord: it starts over.
  if (!withinRange(h.x, h.y, c.x, c.y, r.reach) || h.moving || (w.held > 0 && h.hp < w.hp)) {
    w.held = 0;
    w.hp = h.hp;
    return;
  }
  w.hp = h.hp;
  if (++w.held < r.read) return;
  const i = w.job;
  w.job = -1;
  w.held = 0;
  touch(s, r, w, i, cue);
};

/** Aster's cairns found (the first doesn't count: it is where she starts). */
export const cairnsGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.cairns;
  const w = s.senda?.cairns;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: imax(0, w.step - 1), need: r.way.length - 1 };
};

/** Won a moment after the last cairn is found (its red thread drawn). */
export const cairnsResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.cairns;
  if (!w || w.done < 0) return null;
  return s.tick >= w.done + 18 ? 'won' : null;
};
